import { useState, useEffect, useRef, useMemo } from "react";
import Select from 'react-select';
import swal from 'sweetalert2';
import * as helper from '../global/helper';
import { getBookingMasters, getBookingTemplates, saveVendorTemplates } from "../api/vendorAPI";

const START_TYPES = [
    { value: 'Slot',    label: 'Staggered' },
    // { value: 'Shotgun', label: 'Shotgun'   },
];

const TEE_BOX_OPTIONS = [
    { value: 1,  label: 'Tee 1'  },
    // { value: 10, label: 'Tee 10' },
];

const TYPE_OPTIONS = [
    { value: 1, label: 'Weekday' },
    { value: 2, label: 'Weekend' },
];

const FILTERS = ['All', 'Assigned', 'Unassigned', 'Active', 'Inactive'];

const VendorTemplatesTab = ({ vendorID, onDirtyChange }) => {

    const [bookingMasters,      setBookingMasters]      = useState([]);
    const [selectedStartType,   setSelectedStartType]   = useState(START_TYPES[0]);
    const [selectedMaster,      setSelectedMaster]      = useState(null);
    const [selectedTeeBox,      setSelectedTeeBox]      = useState( { value: 1,  label: 'Tee 1'  });
    const [selectedType,        setSelectedType]        = useState( { value: 1, label: 'Weekday' });
    const [templates,           setTemplates]           = useState([]);
    const [isLoadingMasters,    setIsLoadingMasters]    = useState(false);
    const [isLoadingTemplates,  setIsLoadingTemplates]  = useState(false);
    const [filterMode,          setFilterMode]          = useState('All');
    const [isDirty,             setIsDirty]             = useState(false);
    const selectAllRef = useRef(null);

    const isStaggered = selectedStartType?.value === 'Slot';

    useEffect(() => {
        if (vendorID) {
            resetSelections();
            fetchBookingMasters();
        }
    }, [vendorID]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty]);

    // Sync select-all indeterminate state against full template list
    useEffect(() => {
        const el = selectAllRef.current;
        if (!el) return;
        const allChecked  = templates.length > 0 && templates.every(t => t.IsAssigned);
        const someChecked = templates.some(t => t.IsAssigned);
        el.indeterminate  = someChecked && !allChecked;
        el.checked        = allChecked;
    }, [templates]);

    const resetSelections = () => {
        setSelectedStartType(START_TYPES[0]);
        setSelectedMaster(null);
        setSelectedTeeBox( { value: 1,  label: 'Tee 1'  });
        setSelectedType( { value: 1, label: 'Weekday' });
        setTemplates([]);
        setFilterMode('All');
        setIsDirty(false);
    };

    const markDirty = (rows) =>
        rows.some(t => t.IsAssigned !== t._orig.IsAssigned || t.IsActive !== t._orig.IsActive);

    const fetchBookingMasters = async () => {
        setIsLoadingMasters(true);
        try {
            const data = await getBookingMasters();
            setBookingMasters(data?.masters || []);
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message });
        } finally {
            setIsLoadingMasters(false);
        }
    };

    const fetchTemplates = async (master) => {
        setIsLoadingTemplates(true);
        setTemplates([]);
        setIsDirty(false);
        setFilterMode('All');
        setSelectedTeeBox( { value: 1,  label: 'Tee 1'  });
        setSelectedType( { value: 1, label: 'Weekday' });
        try {
            const data = await getBookingTemplates(vendorID, master.value, master.startType);

            // data.MasterTemplates — all slots for this master
            // data.VendorTemplates — vendor-specific assignments (keyed by TemplateMasterID)
            const masterTemplates  = data?.MasterTemplates || [];
            const vendorTemplates  = data?.VendorTemplates || [];

            const vendorMap = {};
            vendorTemplates.forEach(vt => { vendorMap[vt.TemplateID] = vt; });

            setTemplates(masterTemplates.map(t => {
                const vt = vendorMap[t.ID] || {};
                return {
                    ...t,
                    IsAssigned : !!vt.IsAssigned,
                    IsActive   : !!vt.IsActive,
                    Comment    : vt.Comment || '',
                    _orig      : { IsAssigned: !!vt.IsAssigned, IsActive: !!vt.IsActive },
                };
            }));
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message });
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    const handleStartTypeChange = (option) => {
        setSelectedStartType(option);
        setSelectedMaster(null);
        setSelectedTeeBox( { value: 1,  label: 'Tee 1'  });
        setSelectedType( { value: 1, label: 'Weekday' });
        setTemplates([]);
        setIsDirty(false);
        setFilterMode('All');
    };

    const handleMasterChange = (option) => {
        setSelectedMaster(option);
        if (option) fetchTemplates(option);
        else {
            setSelectedTeeBox(null);
            setSelectedType(null);
            setTemplates([]);
            setIsDirty(false);
            setFilterMode('All');
        }
    };

    // Masters dropdown — filtered by start type; for Staggered, only show TeeBox 1 masters
    const masterOptions = useMemo(() =>
        bookingMasters
            .filter(m => !selectedStartType || m.StartType === selectedStartType.value)
            .filter(m => !isStaggered || m.TeeBox == 1)
            .map(m => ({ value: m.ID, label: m.Name, startType: m.StartType })),
        [bookingMasters, selectedStartType, isStaggered]
    );

    const updateRow = (id, field, value) => {
        setTemplates(prev => {
            const next = prev.map(t => {
                if (t.ID !== id) return t;
                const updated = { ...t, [field]: value };
                // if (field === 'IsAssigned' && !value) updated.IsActive = false;
                if (field === 'IsAssigned') updated.IsActive = value;
                return updated;
            });
            setIsDirty(markDirty(next));
            return next;
        });
    };

    const handleSelectAll = (checked) => {
        setTemplates(prev => {
            const next = prev.map(t => ({
                ...t,
                IsAssigned : checked,
                IsActive   : checked ? t.IsActive : false,
            }));
            setIsDirty(markDirty(next));
            return next;
        });
    };

    const handleSave = async () => {
        const btn = $('#btnSaveTemplates');
        helper.StartProcessing(btn);
        try {
            const payload = templates
                .filter(t => t.IsAssigned !== t._orig.IsAssigned || t.IsActive !== t._orig.IsActive)
                .map(t => ({
                    VendorID   : vendorID,
                    MasterID   : selectedMaster.value,
                    TemplateID : t.ID,
                    IsAssigned : t.IsAssigned,
                    IsActive   : t.IsActive,
                    Type       : selectedStartType?.value?.toUpperCase(),
                    Comment    : t.Comment || ''
                }));

            // console.log('payload', payload);
            // helper.StopProcessing(btn);
            // return

            await saveVendorTemplates(payload);
            helper.StopProcessing(btn);
            setTemplates(prev => prev.map(t => ({
                ...t,
                _orig: { IsAssigned: t.IsAssigned, IsActive: t.IsActive },
            })));
            setIsDirty(false);
            swal.fire({
                toast             : true,
                icon              : 'success',
                titleText         : 'Vendor templates saved successfully!',
                position          : 'bottom-end',
                showConfirmButton : false,
                timer             : 1500,
                animation         : false,
                customClass       : { popup: 'margin-20' },
            });
        } catch (err) {
            helper.StopProcessing(btn);
            swal.fire({
                icon               : 'error',
                titleText          : 'Error!',
                text               : err.message,
                buttonsStyling     : false,
                confirmButtonClass : 'btn btn-brand',
            });
        }
    };

    // Apply TeeBox + Type pre-filter (Staggered only), then mode filter
    const preFiltered = useMemo(() => {
        return templates.filter(t => {
            if (isStaggered && selectedTeeBox && t.TeeBox    !== selectedTeeBox.value) return false;
            if (isStaggered && selectedType   && t.TeeTypeID !== selectedType.value)   return false;
            return true;
        });
    }, [templates, isStaggered, selectedTeeBox, selectedType]);

    const displayList = useMemo(() => {
        return preFiltered.filter(t => {
            switch (filterMode) {
                case 'Assigned':   return  t.IsAssigned;
                case 'Unassigned': return !t.IsAssigned;
                case 'Active':     return  t.IsAssigned &&  t.IsActive;
                case 'Inactive':   return  t.IsAssigned && !t.IsActive;
                default:           return true;
            }
        });
    }, [preFiltered, filterMode]);

    const filterCounts = useMemo(() => ({
        All        : preFiltered.length,
        Assigned   : preFiltered.filter(t =>  t.IsAssigned).length,
        Unassigned : preFiltered.filter(t => !t.IsAssigned).length,
        Active     : preFiltered.filter(t =>  t.IsAssigned &&  t.IsActive).length,
        Inactive   : preFiltered.filter(t =>  t.IsAssigned && !t.IsActive).length,
    }), [preFiltered]);

    return (
        <div className="margin-t10">

            {/* Row 1 — StartType + Master */}
            <div className="row margin-b10 align-items-end">
                <div className="col-lg-2">
                    <label className="col-form-label kt-font-bold" style={{ fontSize: 12 }}>Start Type</label>
                    <Select
                        id="ddlStartType"
                        name="ddlStartType"
                        className="multi-custom"
                        placeholder="Start type..."
                        isLoading={isLoadingMasters}
                        value={selectedStartType}
                        onChange={handleStartTypeChange}
                        options={START_TYPES}
                    />
                </div>
                <div className="col-lg-3">
                    <label className="col-form-label kt-font-bold" style={{ fontSize: 12 }}>Master</label>
                    <Select
                        id="ddlMaster"
                        name="ddlMaster"
                        className="multi-custom"
                        placeholder={selectedStartType ? 'Select master...' : 'Select start type first'}
                        isDisabled={!selectedStartType || isLoadingMasters}
                        isLoading={isLoadingMasters}
                        value={selectedMaster}
                        onChange={handleMasterChange}
                        options={masterOptions}
                    />
                </div>

                {/* Row 2 — TeeBox + Type filters (Staggered only) */}
                {isStaggered && selectedMaster && !isLoadingTemplates && (<>                  
                        <div className="col-lg-2">
                            <label className="col-form-label kt-font-bold" style={{ fontSize: 12 }}>Tee Box</label>
                            <Select
                                id="ddlTeeBox"
                                name="ddlTeeBox"
                                className="multi-custom"
                                placeholder="All tee boxes"
                                isDisabled={true}
                                // isClearable
                                value={selectedTeeBox}
                                onChange={(opt) => setSelectedTeeBox(opt)}
                                options={TEE_BOX_OPTIONS}
                            />
                        </div>
                        <div className="col-lg-3">
                            <label className="col-form-label kt-font-bold" style={{ fontSize: 12 }}>Type</label>
                            <Select
                                id="ddlType"
                                name="ddlType"
                                className="multi-custom"
                                placeholder="All types"
                                // isClearable
                                value={selectedType}
                                onChange={(opt) => setSelectedType(opt)}
                                options={TYPE_OPTIONS}
                            />
                        </div>
                    
                </>)}
            </div>

            

            {/* Loading spinner */}
            {isLoadingTemplates && (
                <div className="text-center padding-tb20">
                    <div className="kt-spinner kt-spinner--brand kt-spinner--lg margin-auto"></div>
                </div>
            )}

            {/* Templates section */}
            {!isLoadingTemplates && selectedMaster && (
                <>
                    {/* Filter buttons */}
                    <div className="row margin-b10">
                        <div className="col-12 text-right">
                            {FILTERS.map(f => (
                                <button
                                    key={f}
                                    type="button"
                                    className={`btn btn-sm mr-1 ${filterMode === f ? 'btn-brand' : 'btn-outline-brand'} d-inline-flex`}
                                    onClick={() => setFilterMode(f)}
                                >
                                    {f}
                                    {filterMode == f && (
                                        <span className="kt-badge kt-badge--unified-light kt-badge--inline margin-l5">
                                            {filterCounts[f]}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                        <table className="table table-striped table-bordered table-hover table-sm margin-b0">
                            <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                                <tr>
                                    <th style={{ width: 44, textAlign: 'center', verticalAlign: 'middle' }}>
                                        {/* <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            title="Select / Deselect All"
                                            style={{ cursor: 'pointer' }}
                                        /> 
                                         */}
                                        ALL
                                    </th>
                                    <th style={{ width: 60, textAlign: 'center', verticalAlign: 'middle' }} >{isStaggered ? 'Slot Time' : 'Hole'}</th>
                                    {isStaggered && (<th style={{ width: 60, textAlign: 'center', verticalAlign: 'middle' }} > TeeBox </th>)}
                                    <th style={{ width: 80, textAlign: 'center' }}>Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayList.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="text-center padding-tb20">
                                            <span className="kt-font-bold kt-font-lg text-muted">No templates found.</span>
                                        </td>
                                    </tr>
                                ) : displayList.map(t => (
                                    <tr key={t.ID}>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            <input
                                                type="checkbox"
                                                checked={t.IsAssigned}
                                                style={{ cursor: 'pointer' }}
                                                onChange={(e) => updateRow(t.ID, 'IsAssigned', e.target.checked)}
                                            />
                                        </td>
                                        <td style={{textAlign: 'center', verticalAlign: 'middle' }}>
                                            {isStaggered ? (t.Slot || '-') : (t.Hole || '-')}
                                        </td>

                                        {isStaggered && (<td style={{textAlign: 'center', verticalAlign: 'middle' }} > {t.TeeBox} </td>)}

                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            <span className="kt-switch kt-switch--icon kt-switch--sm">
                                                <label style={{ opacity: t.IsAssigned ? 1 : 0.4, pointerEvents: t.IsAssigned ? 'auto' : 'none' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={t.IsActive}
                                                        disabled={!t.IsAssigned}
                                                        onChange={(e) => updateRow(t.ID, 'IsActive', e.target.checked)}
                                                    />
                                                    <span></span>
                                                </label>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Save row */}
                    <div className="row margin-t10">
                        <div className="col-12 text-right">
                            {isDirty && (
                                <span className="kt-font-warning margin-r10" style={{ fontSize: 13 }}>
                                    <i className="la la-exclamation-circle"></i> Unsaved changes
                                </span>
                            )}
                            <button
                                id="btnSaveTemplates"
                                type="button"
                                className="btn btn-brand btn-sm"
                                onClick={handleSave}
                            >
                                <i className="la la-save"></i> Save Templates
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Placeholder when nothing selected */}
            {!isLoadingTemplates && !selectedMaster && !isLoadingMasters && (
                <div className="text-center padding-tb20">
                    <span className="text-muted" style={{ fontSize: 13 }}>
                        <i className="la la-info-circle margin-r5"></i>
                        Select a Start Type and Master to load templates.
                    </span>
                </div>
            )}

        </div>
    );
};

export default VendorTemplatesTab;
